import {
  OperationVariables,
  QueryHookOptions,
  QueryResult,
  useQuery,
} from "@apollo/client";

/**
 * This is how useQuery behaved in apollo-client v2.
 * https://github.com/apollographql/apollo-client/pull/6566
 *
 * In short, it will return the previous data if the query is loading and there is no data.
 * This is useful for cases where showing data from an older parameter set of a query is more valuable
 * than showing no data or a loading state (e.g. showing a previous search result while a new search is loading).
 * In general, use useQuery instead unless there's a clear need to maintain previous data when query parameters change.
 */
export const useQueryWithPreviousData = <
  TData = any,
  TVariables extends OperationVariables = any,
>(
  query: any,
  options?: QueryHookOptions<TData, TVariables>,
): QueryResult<TData, TVariables> => {
  const result = useQuery<TData, TVariables>(query, options);
  if ("previousData" in result && result.loading && !result.data) {
    return {
      ...result,
      data: result.previousData,
    };
  }
  return result;
};
